import { useState, useCallback } from "react"
import { Form } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { useEngine } from "hooks/api/useEngine"
import { useExportMap } from "features/QueryConsole/api/useExportMap"
import { downloadResponseAsFile } from "utils/files/downloadUtils"
import { QueryInstance } from "types/engine.types"
import {
  SPSSFormValues,
  CSVFormValues,
  EXPORT_VARIABLE_TYPES,
  EXPORT_FORMAT,
  SPS_EXPORT_TYPE,
  DATA_FORMAT,
  CSV_CAT_OPTION,
  JSONL_FLAT_SEPARATOR,
} from "types/engineExport.types"

export interface InputValues {
  filename: string
  variableType: EXPORT_VARIABLE_TYPES | ""
  dataFormat: DATA_FORMAT
  csvCatOption: CSV_CAT_OPTION
  jsonlUseLabels: boolean
  jsonlFlatSeparator: JSONL_FLAT_SEPARATOR
  spsOutputFormat: "sps" | "sav"
  csvVariableType: EXPORT_VARIABLE_TYPES | ""
}

interface FilenameFormValues {
  filename: string
}

export const useExportDataModal = (queryInstance: QueryInstance | null) => {
  const [inputValues, setInputValues] = useState<InputValues>({
    filename: "",
    variableType: "",
    dataFormat: DATA_FORMAT.CATEGORIZED,
    csvCatOption: CSV_CAT_OPTION.NORMAL,
    jsonlUseLabels: false,
    jsonlFlatSeparator: JSONL_FLAT_SEPARATOR.NONE,
    spsOutputFormat: "sps",
    csvVariableType: "",
  })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isExportMapModalOpen, setIsExportMapModalOpen] = useState(false)
  const [exportMapFormat, setExportMapFormat] = useState<"sps" | "sav" | "csv">("sps")

  const {
    exportJSON, isExportingJSON,
    exportJSONL, isExportingJSONL,
    exportSPS, isExportingSPS,
    exportSAV, isExportingSAV,
    exportDQD, isExportingDQD,
  } = useEngine()
  const { exportWithCustomMap, exportCsvDirect, isExportingCsvDirect } = useExportMap()

  const [spssForm] = Form.useForm<SPSSFormValues>()
  const [csvForm] = Form.useForm<CSVFormValues>()
  const [filenameForm] = Form.useForm<FilenameFormValues>()

  const handleInputChange = <K extends keyof InputValues>(
    key: K,
    value: InputValues[K]
  ): void => {
    setInputValues((prev) => ({ ...prev, [key]: value }))
  }

  const validateFilename = useCallback(async (): Promise<boolean> => {
    try {
      await filenameForm.validateFields()
      return true
    } catch {
      return false
    }
  }, [filenameForm])

  const handleJSONExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await exportJSON(queryInstance, inputValues.filename, inputValues.dataFormat)
      notify.toast.success("JSON export completed successfully")
    } catch (error) {
      console.error("JSON export failed:", error)
      notify.toast.error("JSON export failed")
    }
  }

  const handleJSONLExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await exportJSONL(queryInstance, inputValues.filename, inputValues.jsonlUseLabels, inputValues.jsonlFlatSeparator)
      notify.toast.success("JSONL export completed successfully")
    } catch (error) {
      console.error("JSONL export failed:", error)
      notify.toast.error("JSONL export failed")
    }
  }

  const handleSPSSExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await spssForm.validateFields()
      await exportSPS(
        queryInstance,
        inputValues.variableType as EXPORT_VARIABLE_TYPES,
        inputValues.filename
      )
      notify.toast.success("SPSS export completed successfully")
    } catch (error) {
      console.error("SPSS export failed:", error)
      notify.toast.error("SPSS export failed")
    }
  }

  const handleSAVExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await spssForm.validateFields()
      await exportSAV(
        queryInstance,
        inputValues.variableType as EXPORT_VARIABLE_TYPES,
        inputValues.filename
      )
      notify.toast.success("SAV export completed successfully")
    } catch (error) {
      console.error("SAV export failed:", error)
      notify.toast.error("SAV export failed")
    }
  }

  const handleCSVExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await csvForm.validateFields()
      const varType =
        inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
          ? EXPORT_VARIABLE_TYPES.NUMBERING
          : EXPORT_VARIABLE_TYPES.LABELS
      const response = await exportCsvDirect(queryInstance, varType, inputValues.csvCatOption, inputValues.filename || "export")
      downloadResponseAsFile(response, `${inputValues.filename || "export"}.zip`)
      notify.toast.success("CSV export completed successfully")
    } catch (error) {
      console.error("CSV export failed:", error)
      notify.toast.error("CSV export failed")
    }
  }

  const handleDQDExport = async (): Promise<void> => {
    if (!queryInstance) return
    const isValid = await validateFilename()
    if (!isValid) return
    try {
      await exportDQD(queryInstance, inputValues.filename, inputValues.dataFormat)
      notify.toast.success("DQD export completed successfully")
    } catch (error) {
      console.error("DQD export failed:", error)
      notify.toast.error("DQD export failed")
    }
  }

  const handleOpenExportMap = useCallback(
    async (format: "sps" | "sav" | "csv") => {
      const isValid = await validateFilename()
      if (!isValid) {
        notify.toast.error("Please enter a filename first")
        return
      }
      try {
        if (format === "csv") {
          await csvForm.validateFields()
        } else {
          await spssForm.validateFields()
        }
        setExportMapFormat(format)
        setIsExportMapModalOpen(true)
      } catch {
        notify.toast.error("Please select a variable type first")
      }
    },
    [validateFilename, spssForm, csvForm]
  )

  const handleExportWithMap = useCallback(
    async (exportMapCsv: string): Promise<void> => {
      if (!queryInstance) return
      const filename = inputValues.filename || "export"
      const formatMap = {
        sps: EXPORT_FORMAT.SPS,
        sav: EXPORT_FORMAT.SAV,
        csv: EXPORT_FORMAT.CSV,
      }

      const varType = exportMapFormat === "csv"
        ? (inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
            ? EXPORT_VARIABLE_TYPES.NUMBERING
            : EXPORT_VARIABLE_TYPES.LABELS)
        : (inputValues.variableType === EXPORT_VARIABLE_TYPES.NUMBERING
            ? EXPORT_VARIABLE_TYPES.NUMBERING
            : EXPORT_VARIABLE_TYPES.LABELS)

      const response = await exportWithCustomMap(queryInstance, exportMapCsv, {
        format: formatMap[exportMapFormat],
        varType,
        spsType: SPS_EXPORT_TYPE.SPS_DAT,
        filename,
        catOption: inputValues.csvCatOption,
      })
      downloadResponseAsFile(response, `${filename}.zip`)
      const formatLabel = exportMapFormat === "sps" ? "SPSS" : exportMapFormat === "sav" ? "SAV" : "CSV"
      notify.toast.success(`${formatLabel} export completed successfully`)
      setIsModalVisible(false)
    },
    [queryInstance, exportWithCustomMap, inputValues, exportMapFormat]
  )

  return {
    inputValues,
    handleInputChange,
    isModalVisible,
    setIsModalVisible,
    isExportMapModalOpen,
    setIsExportMapModalOpen,
    exportMapFormat,
    filenameForm,
    spssForm,
    csvForm,
    // Export handlers
    handleJSONExport,
    handleJSONLExport,
    handleSPSSExport,
    handleSAVExport,
    handleCSVExport,
    handleDQDExport,
    handleOpenExportMap,
    handleExportWithMap,
    // Loading states
    isExportingJSON,
    isExportingJSONL,
    isExportingSPS,
    isExportingSAV,
    isExportingDQD,
    isExportingCsvDirect,
  }
}
